import { create } from 'zustand';
import { apiClient, type PhotoPatch } from '../services/apiClient';
import { PhotosByEvent, Season, Event, ViewerMode } from '../types';
import { SeasonCollection } from '../types/seasonTypes';
import { organizeSeasonCollection } from '../utils/seasonCollection';
import { makeSeasonId } from '../utils/seasonDomain';
import { sortEventsByMonth, sortSeasonsByYear } from '../utils/sortingUtils';
import { useViewerModeStore } from './useViewerModeStore';

interface SeasonsState {
  // Core data
  seasons: Season[];
  eventsBySeason: Record<string, Event[]>;
  photosByEvent: PhotosByEvent;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSeasons: () => Promise<void>;
  fetchEventsBySeason: (seasonId: string) => Promise<void>;
  fetchAllEvents: () => Promise<void>;
  fetchPhotos: (mode: ViewerMode) => Promise<void>;
  createSeason: (name: string, year: number) => Promise<void>;
  deleteSeason: (seasonId: string) => Promise<void>;
  createEvent: (name: string, month: string, seasonId: string) => Promise<void>;
  updateEvent: (id: number, updates: { name?: string; month?: string }) => Promise<void>;
  deleteEvent: (eventId: number) => Promise<void>;
  
  // CRITICAL: State synchronization method for photosByEvent keys
  updatePhotosByEventKey: (oldEventName: string, newEventName: string) => void;

  // Ensure photosByEvent contains every event.
  syncPhotosByEventWithAllEvents: () => void;

  // Photo management
  uploadPhoto: (eventName: string, month: string, seasonId: string, file: File, caption?: string) => Promise<void>;
  updatePhoto: (id: number, patch: PhotoPatch) => Promise<void>;
  deletePhoto: (id: number) => Promise<void>;
  reorderPhotos: (eventName: string, photoIds: number[]) => Promise<void>;

  // Computed/utility methods
  getOrganizedSeasonData: (mode: ViewerMode) => SeasonCollection;
  getSeasonById: (seasonId: string) => Season | undefined;
  seasonExists: (name: string, year: number) => boolean; // Check if season exists
  clearError: () => void;
  clearData: () => void;
}

function findEventLocation(eventsBySeason: Record<string, Event[]>, id: number): { event: Event; seasonId: string } | null {
  for (const [seasonId, events] of Object.entries(eventsBySeason)) {
    const event = events.find(e => e.id === id);
    if (event) {
      return { event: { ...event }, seasonId };
    }
  }
  return null;
}

export const useSeasonsStore = create<SeasonsState>((set, get) => ({
  seasons: [],
  eventsBySeason: {},
  photosByEvent: {},
  isLoading: false,
  error: null,
  clearData: () => set({ seasons: [], eventsBySeason: {}, photosByEvent: {}, isLoading: false, error: null }),

  fetchSeasons: async () => {
    set({ isLoading: true, error: null });
    try {
      const seasons = await apiClient.getAllSeasons();
      set({
        seasons,
        isLoading: false
      });
    } catch (error) {
      console.error('Failed to fetch seasons:', error);
      set({
        error: (error as Error).message,
        isLoading: false
      });
    }
  },

  fetchEventsBySeason: async (seasonId: string) => {
    try {
      const { events } = await apiClient.getEventsBySeason(seasonId);
      set(state => ({
        eventsBySeason: {
          ...state.eventsBySeason,
          [seasonId]: events
        }
      }));
    } catch (error) {
      console.error(`Failed to fetch events for season ${seasonId}:`, error);
      set({ error: (error as Error).message });
    }
  },

  fetchPhotos: async (mode: ViewerMode) => {
    try {
      const photos = await apiClient.getAllPhotos(mode);
      // Latest wins: a slow fetch resolving after the viewer switched modes
      // must not overwrite the manifest the current mode already landed.
      if (useViewerModeStore.getState().mode !== mode) {
        return;
      }
      set({
        photosByEvent: photos
      });
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      set({ error: (error as Error).message });
    }
  },

  // CRITICAL: Add photosByEvent key synchronization method
  updatePhotosByEventKey: (oldEventName: string, newEventName: string) => {
    set(state => {
      const newPhotosByEvent = { ...state.photosByEvent };

      // Move data from old key to new key
      if (newPhotosByEvent[oldEventName]) {
        newPhotosByEvent[newEventName] = {
          ...newPhotosByEvent[oldEventName],
          event: {
            ...newPhotosByEvent[oldEventName].event,
            name: newEventName // Update the event name in the data too
          }
        };
        delete newPhotosByEvent[oldEventName];
      }

      return { photosByEvent: newPhotosByEvent };
    });
  },

  // Ensure photosByEvent contains every event.
  syncPhotosByEventWithAllEvents: () => {
    // Placeholders carry unfiltered event names and exist for admin work, so
    // they must never reach a family manifest — including when a personal
    // fetch resolves after the viewer has already switched.
    if (useViewerModeStore.getState().mode !== 'personal') {
      return;
    }

    const state = get();
    const newPhotosByEvent = { ...state.photosByEvent };

    // Iterate through all events in all seasons
    Object.entries(state.eventsBySeason).forEach(([seasonId, events]) => {
      const season = state.seasons.find(s => s.id === seasonId);

      if (!season) {
        return;
      }

      events.forEach(event => {
        // If event doesn't exist in photosByEvent, add it as placeholder
        if (!newPhotosByEvent[event.name]) {
          newPhotosByEvent[event.name] = {
            event: {
              id: event.id,
              name: event.name,
              month: event.month,
              year: season.year,
              seasonId: event.seasonId,
              createdAt: event.createdAt,
              updatedAt: event.updatedAt
            },
            season: seasonId,
            photos: [], // Empty array for events without photos
            avatarPhotoId: null
          };
        }
      });
    });

    set({ photosByEvent: newPhotosByEvent });
  },

  createSeason: async (name: string, year: number) => {
    const newSeason: Season = {
      id: makeSeasonId(name, year),
      name,
      year,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Optimistic update - add season immediately
    set(state => ({
      seasons: sortSeasonsByYear([...state.seasons, newSeason]),
      isLoading: true,
      error: null
    }));

    try {
      const createdSeason = await apiClient.createSeason(name, year);

      // Update with server response
      set(state => ({
        seasons: state.seasons.map(s =>
          s.id === newSeason.id ? createdSeason : s
        ),
        isLoading: false
      }));
    } catch (error) {
      // Rollback optimistic update on error
      set(state => ({
        seasons: state.seasons.filter(s => s.id !== newSeason.id),
        error: (error as Error).message,
        isLoading: false
      }));
      throw error;
    }
  },

  deleteSeason: async (seasonId: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.deleteSeason(seasonId);

      // Server confirmed; drop the season locally
      set(state => ({
        seasons: state.seasons.filter(s => s.id !== seasonId),
        eventsBySeason: Object.fromEntries(
          Object.entries(state.eventsBySeason).filter(([id]) => id !== seasonId)
        ),
        isLoading: false
      }));
    } catch (error) {
      console.error('Failed to delete season:', error);
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  createEvent: async (name: string, month: string, seasonId: string) => {
    set({ isLoading: true, error: null });
    try {
      const createdEvent = await apiClient.createEvent(name, month, seasonId);

      await get().fetchEventsBySeason(seasonId);

      // CRITICAL: Immediately add to photosByEvent as placeholder
      const season = get().seasons.find(s => s.id === seasonId);
      if (season) {
        set(state => ({
          photosByEvent: {
            ...state.photosByEvent,
            [name]: {
              event: {
                id: createdEvent.id,
                name: createdEvent.name,
                month: createdEvent.month,
                year: season.year,
                seasonId: createdEvent.seasonId,
                createdAt: createdEvent.createdAt,
                updatedAt: createdEvent.updatedAt
              },
              season: seasonId,
              photos: [],
              avatarPhotoId: null
            }
          },
          isLoading: false
        }));
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('Failed to create event:', error);
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  // ROBUST: Enhanced updateEvent with state synchronization and comprehensive race condition prevention
  /** Validates, sends the update, then replaces the event (and its photo-bucket key on rename) in local state. */
  updateEvent: async (id: number, updates: { name?: string; month?: string }) => {
    // STORE-LEVEL: Prevent concurrent store operations
    if (get().isLoading) {
      console.warn('updateEvent called while store is loading, skipping');
      return;
    }

    // Set loading state to prevent other store operations
    set({ isLoading: true, error: null });

    // Store original data for SPECIFIC rollback (not entire store)
    const location = findEventLocation(get().eventsBySeason, id);

    if (!location) {
      set({ error: 'Event not found for update', isLoading: false });
      throw new Error('Event not found for update');
    }

    const { event: originalEvent, seasonId: seasonIdWithEvent } = location;
    const originalEventName = originalEvent.name;

    try {
      // OPTIMISTIC UPDATE: Update eventsBySeason immediately for UI responsiveness
      set(state => {
        const newEventsBySeason = { ...state.eventsBySeason };

        const eventIndex = newEventsBySeason[seasonIdWithEvent].findIndex(e => e.id === id);
        if (eventIndex !== -1) {
          const updatedEvent = { ...newEventsBySeason[seasonIdWithEvent][eventIndex], ...updates };
          newEventsBySeason[seasonIdWithEvent] = [...newEventsBySeason[seasonIdWithEvent]];
          newEventsBySeason[seasonIdWithEvent][eventIndex] = updatedEvent;

          // Re-sort if month changed - use shared utility
          if (updates.month) {
            const seasonInfo = get().getSeasonById(seasonIdWithEvent);
            const sortedEvents = sortEventsByMonth(
              newEventsBySeason[seasonIdWithEvent],
              seasonInfo?.name
            );
            newEventsBySeason[seasonIdWithEvent] = sortedEvents;
          }
        }

        return { eventsBySeason: newEventsBySeason };
      });

      // CRITICAL: If name is changing, immediately synchronize photosByEvent keys
      if (updates.name && updates.name !== originalEventName) {
        get().updatePhotosByEventKey(originalEventName, updates.name);
      }

      const updatedEvent = await apiClient.updateEvent(id, updates);

      // FINAL CONFIRMATION: Update with server response
      set(state => {
        const newEventsBySeason = { ...state.eventsBySeason };
        const eventIndex = newEventsBySeason[seasonIdWithEvent].findIndex(e => e.id === id);
        if (eventIndex !== -1) {
          newEventsBySeason[seasonIdWithEvent][eventIndex] = updatedEvent;
        }
        return {
          eventsBySeason: newEventsBySeason,
          isLoading: false
        };
      });

    } catch (error) {
      // GRANULAR ROLLBACK: Only restore the specific failed event and its photosByEvent key
      console.error('updateEvent failed, rolling back changes:', error);

      set(state => {
        const newEventsBySeason = { ...state.eventsBySeason };
        const eventIndex = newEventsBySeason[seasonIdWithEvent].findIndex(e => e.id === id);
        if (eventIndex !== -1) {
          newEventsBySeason[seasonIdWithEvent] = [...newEventsBySeason[seasonIdWithEvent]];
          newEventsBySeason[seasonIdWithEvent][eventIndex] = originalEvent;
        }

        return {
          eventsBySeason: newEventsBySeason,
          error: (error as Error).message,
          isLoading: false
        };
      });

      // ROLLBACK CLARIFICATION: Also rollback photosByEvent key if name was changed
      if (updates.name && updates.name !== originalEventName) {
        get().updatePhotosByEventKey(updates.name, originalEventName);
      }

      throw error;
    }
  },

  deleteEvent: async (eventId: number) => {
    try {
      const eventNameToDelete = findEventLocation(get().eventsBySeason, eventId)?.event.name ?? null;

      await apiClient.deleteEvent(eventId);

      set(state => {
        const newEventsBySeason = { ...state.eventsBySeason };
        const newPhotosByEvent = { ...state.photosByEvent };

        Object.keys(newEventsBySeason).forEach(seasonId => {
          const filteredEvents = newEventsBySeason[seasonId].filter(e => e.id !== eventId);
          const seasonInfo = state.seasons.find(s => s.id === seasonId);
          newEventsBySeason[seasonId] = sortEventsByMonth(filteredEvents, seasonInfo?.name);
        });

        if (eventNameToDelete && newPhotosByEvent[eventNameToDelete]) {
          delete newPhotosByEvent[eventNameToDelete];
        }

        return {
          eventsBySeason: newEventsBySeason,
          photosByEvent: newPhotosByEvent
        };
      });
    } catch (error) {
      console.error('Failed to delete event:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  uploadPhoto: async (eventName: string, month: string, seasonId: string, file: File, caption?: string) => {
    try {
      await apiClient.uploadPhoto(eventName, month, seasonId, file, caption);

      // Refresh photos to get updated data; uploads exist only under the
      // admin session, which is personal mode by definition.
      const photos = await apiClient.getAllPhotos('personal');
      set({ photosByEvent: photos });
    } catch (error) {
      console.error('Failed to upload photo:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  updatePhoto: async (id: number, patch: PhotoPatch) => {
    try {
      await apiClient.updatePhoto(id, patch);

      set(state => {
        const newPhotosByEvent = { ...state.photosByEvent };
        Object.keys(newPhotosByEvent).forEach(eventName => {
          if (!newPhotosByEvent[eventName].photos.some(p => p.id === id)) {
            return;
          }
          newPhotosByEvent[eventName] = {
            ...newPhotosByEvent[eventName],
            photos: newPhotosByEvent[eventName].photos.map(p =>
              p.id === id ? { ...p, ...patch } : p
            )
          };
        });
        return { photosByEvent: newPhotosByEvent };
      });
    } catch (error) {
      console.error('Failed to update photo:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  deletePhoto: async (id: number) => {
    try {
      await apiClient.deletePhoto(id);

      set(state => {
        const newPhotosByEvent = { ...state.photosByEvent };
        Object.keys(newPhotosByEvent).forEach(eventName => {
          newPhotosByEvent[eventName] = {
            ...newPhotosByEvent[eventName],
            photos: newPhotosByEvent[eventName].photos.filter(p => p.id !== id)
          };

          // Remove event if no photos left
          if (newPhotosByEvent[eventName].photos.length === 0) {
            delete newPhotosByEvent[eventName];
          }
        });
        return { photosByEvent: newPhotosByEvent };
      });
    } catch (error) {
      console.error('Failed to delete photo:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  reorderPhotos: async (eventName: string, photoIds: number[]) => {
    try {
      const photos = await apiClient.reorderPhotos(eventName, photoIds);

      // Replace with the server's canonical order; the avatar is derived
      // from the first photo, mirroring getAllPhotos.
      set(state => {
        const entry = state.photosByEvent[eventName];
        if (!entry) {
          return {};
        }
        return {
          photosByEvent: {
            ...state.photosByEvent,
            [eventName]: { ...entry, photos, avatarPhotoId: photos[0]?.id ?? null }
          }
        };
      });
    } catch (error) {
      console.error('Failed to reorder photos:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  getOrganizedSeasonData: (mode: ViewerMode): SeasonCollection => {
    const state = get();
    return organizeSeasonCollection(state.photosByEvent, state.seasons, mode);
  },

  getSeasonById: (seasonId: string) => {
    return get().seasons.find(s => s.id === seasonId);
  },

  seasonExists: (name: string, year: number): boolean => {
    const seasonId = makeSeasonId(name, year);
    return get().seasons.some(season => season.id === seasonId);
  },

  clearError: () => set({ error: null }),

  fetchAllEvents: async () => {
    const state = get();
    const seasonIds = state.seasons.map(s => s.id);

    await Promise.all(
      seasonIds.map(seasonId => get().fetchEventsBySeason(seasonId))
    );
  },
}));
